import AddLedgerBalanceMonitorDialogProvider, {
  useDialogProviderContext,
} from "src/features/DialogProvider";
import { useAlerting, useTranslation } from "src/hooks";
import Content from "./content";
import { useEffect, useState } from "react";
import useValidation from "./hooks/useValidation";
import { usePageConfigProviderContext } from "src/features/PageConfigProvider";
import { useChains } from "src/components/FetchConfig";
import {
  amountToCryptoAmount,
  findDecimalByChainNameAndAsset,
} from "src/utils/helper";
import EnumCriteria from "@wallet-manager/node-types/dist/src/postgres/const/Criteria";
import { addLedgerBalanceMonitor } from "src/api/merchant";
import { appError } from "src/utils/errorHandler";
import { Action, AlertType } from "src/api/types";

export type AddLedgerBalanceMonitorFields = {
  chainName: string;
  currency: string;
  clientId: string;
  alertType: AlertType;
  interval?: string;
  balance?: string;
  thresholdLower?: string;
  thresholdUpper?: string;
};

const initFields: AddLedgerBalanceMonitorFields = {
  chainName: "",
  currency: "",
  clientId: "",
  alertType: AlertType.FixedThreshold,
  thresholdLower: "",
  thresholdUpper: "",
};

function AddLedgerBalanceMonitorDialogDialog() {
  const [fields, setFields] =
    useState<AddLedgerBalanceMonitorFields>(initFields);
  const { translatePrefix, refreshData } = usePageConfigProviderContext();
  const { t, tc } = useTranslation(translatePrefix);
  const { validate } = useValidation();
  const { alerting } = useAlerting();

  const { isOpen, setIsOpen } = useDialogProviderContext();

  const { selectChainByPieces } = useChains();

  useEffect(() => {
    if (!isOpen) {
      setFields(initFields);
    }
  }, [isOpen]);

  const { chain_type, chain_id } = selectChainByPieces({
    chain_name: fields.chainName,
  });

  const onConfirm = async () => {
    const isValid = validate(fields);

    if (!isValid || chain_type === undefined || chain_id === undefined) {
      return;
    }

    const decimal = findDecimalByChainNameAndAsset(
      String(chain_type),
      chain_id,
      fields.currency,
    );

    const getDetails = () => {
      if (fields.alertType === AlertType.FixedThreshold) {
        const lowerThresholdDetail = fields.thresholdLower
          ? [
              {
                action: Action.Upsert,
                criteria: EnumCriteria.LessThan,
                thresholdValue: amountToCryptoAmount(
                  fields.thresholdLower,
                  Number(decimal),
                ),
              },
            ]
          : [];
        const upperThresholdDetail = fields.thresholdUpper
          ? [
              {
                action: Action.Upsert,
                criteria: EnumCriteria.GreaterThan,
                thresholdValue: amountToCryptoAmount(
                  fields.thresholdUpper,
                  Number(decimal),
                ),
              },
            ]
          : [];

        return [...lowerThresholdDetail, ...upperThresholdDetail];
      } else {
        const stepValue = amountToCryptoAmount(
          fields.interval ?? "0",
          Number(decimal),
        );
        const balanceAmount = amountToCryptoAmount(
          fields.balance ?? "0",
          Number(decimal),
        );
        return [
          {
            action: Action.Upsert,
            criteria: EnumCriteria.SlidingLessThan,
            stepValue: stepValue,
            balance: balanceAmount,
          },
          {
            action: Action.Upsert,
            criteria: EnumCriteria.SlidingGreaterThan,
            stepValue: stepValue,
            balance: balanceAmount,
          },
        ];
      }
    };

    const res = await addLedgerBalanceMonitor({
      assetName: fields.currency,
      clientId: fields.clientId,
      details: getDetails(),
      chainType: chain_type,
      chainId: chain_id,
    });

    const errorRes = res as unknown as {
      error?: { code: number };
      result: null;
      requestId?: string;
    };

    if (res.error?.code === -51) {
      alerting("warning", t("monitor_already_exists_warning"));
      return;
    }

    if (res.error && !res.result) {
      appError({ ...res.error, requestId: res.requestId });
      return;
    }

    alerting("success", t("add_ledger_balance_monitor_success"));
    refreshData();
    setIsOpen(false);
  };

  const isLowerThresholdInvalid =
    fields.alertType === AlertType.FixedThreshold &&
    (Number(fields.thresholdLower) < 0 || isNaN(Number(fields.thresholdLower)));
  const isUpperThresholdInvalid =
    fields.alertType === AlertType.FixedThreshold &&
    fields.thresholdUpper !== "" &&
    (Number(fields.thresholdUpper) <= 0 ||
      isNaN(Number(fields.thresholdUpper)));
  const isIntervalInvalid =
    fields.alertType === AlertType.DynamicThreshold &&
    (!fields.interval ||
      Number(fields.interval) <= 0 ||
      isNaN(Number(fields.interval)));

  const isConfirmDisabled =
    (fields.alertType === AlertType.FixedThreshold &&
      !fields.thresholdLower &&
      !fields.thresholdUpper) ||
    isLowerThresholdInvalid ||
    isUpperThresholdInvalid ||
    isIntervalInvalid;

  return (
    <AddLedgerBalanceMonitorDialogProvider.DialogInOne
      title={t("add_ledger_balance_monitor_dialog_title")}
      content={
        <Content
          fields={fields}
          setFields={setFields}
          isLowerThresholdInvalid={isLowerThresholdInvalid}
          isUpperThresholdInvalid={isUpperThresholdInvalid}
        />
      }
      onConfirm={onConfirm}
      isConfirmDisabled={isConfirmDisabled}
      maxWidth="md"
    />
  );
}

export default AddLedgerBalanceMonitorDialogDialog;
